/**
 * Une écriture : de la pièce aux comptes.
 *
 * L'en-tête dit d'où elle vient (la facture, la note, la reprise), quand elle
 * a été validée et ce qui la corrige ; les lignes disent chaque compte, le
 * tiers, le débit ou le crédit, et la ventilation analytique — avec ce qui
 * n'est pas ventilé, montré plutôt que tu.
 *
 * Aucun geste d'ici : une écriture validée est immuable et se corrige par
 * extourne, qui reste un geste de la base, sur accord.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CornerUpLeft, FileText, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { messageErreur } from '../../../lib/payments/erreur';
import { CHEMINS, ficheEcriture } from '../../../lib/navigation/chemins';
import { lireEcriture, type EcritureDetaillee } from '../../../lib/compta/ecritures/gateway.supabase';
import {
  analytiqueDeLaLigne, controleDesLignes, lienDeLaSource, lienVersLesEcritures, MOIS, NATURES,
  referenceAffichee, STATUTS_ECRITURE,
} from '../../../lib/compta/ecritures/types';
import { dateCourte, euros } from '../components/money';

function dateHeure(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-BE', { timeZone: 'Europe/Brussels', dateStyle: 'short', timeStyle: 'short' });
}

export default function EcriturePage() {
  const { ecritureId = '' } = useParams();
  const location = useLocation();
  const retour = (location.state as { retour?: string } | null)?.retour;

  const [detail, setDetail] = useState<EcritureDetaillee | null>(null);
  const [introuvable, setIntrouvable] = useState(false);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const d = await lireEcriture(ecritureId);
      setDetail(d);
      setIntrouvable(d === null);
    } catch (e) {
      setErreur(messageErreur(e, 'Écriture illisible'));
    } finally {
      setChargement(false);
    }
  }, [ecritureId]);

  useEffect(() => { void charger(); }, [charger]);

  const controle = useMemo(() => (detail ? controleDesLignes(detail.lignes) : null), [detail]);

  const lienRetour = retour ? `${CHEMINS.comptabilite.ecritures}${retour === '?' ? '' : retour}` : CHEMINS.comptabilite.ecritures;

  return (
    <div className="space-y-6">
      <Link to={lienRetour} className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline">
        <ArrowLeft className="h-4 w-4" /> Toutes les écritures
      </Link>

      {chargement ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : erreur ? (
        <div className="rounded-lg bg-red-50 p-4">
          <p className="flex items-start gap-2 text-red-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" /> {erreur}
          </p>
        </div>
      ) : introuvable || !detail ? (
        <div className="rounded-xl bg-white p-8 text-center shadow-md">
          <p className="text-gray-600">Aucune écriture ne porte cet identifiant.</p>
        </div>
      ) : (
        <Fiche detail={detail} controle={controle} />
      )}
    </div>
  );
}

function Fiche({ detail, controle }: {
  detail: EcritureDetaillee;
  controle: ReturnType<typeof controleDesLignes> | null;
}) {
  const { ecriture: e, lignes, references } = detail;
  const source = lienDeLaSource(e);

  return (
    <>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tabular-nums">{referenceAffichee(e)}</h1>
          <p className="text-gray-700">{e.libelle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className={clsx('rounded-full px-3 py-1 text-sm font-medium', STATUTS_ECRITURE[e.statut].classe)}>
            {STATUTS_ECRITURE[e.statut].label}
          </span>
          {e.nature !== 'ordinaire' && (
            <span className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">{NATURES[e.nature] ?? e.nature}</span>
          )}
        </div>
      </header>

      {/* --- L'en-tête ------------------------------------------------------- */}
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Champ terme="Journal">
            <Link to={lienVersLesEcritures({ journal: e.journal, exercice: e.exercice })}
                  className="text-primary-700 hover:underline">{e.journal}</Link>
          </Champ>
          <Champ terme="Date comptable">{dateCourte(e.date)}</Champ>
          <Champ terme="Exercice et période">
            {e.exercice}, {String(e.periode).padStart(2, '0')} — {MOIS[e.periode - 1] ?? e.periode}
          </Champ>
          <Champ terme="Pièce">
            {source ? (
              source.chemin ? (
                <Link to={source.chemin} className="inline-flex items-center gap-1 text-primary-700 hover:underline">
                  <FileText className="h-4 w-4" /> {source.libelle}
                </Link>
              ) : source.libelle
            ) : e.piece ?? '—'}
          </Champ>
          {e.dateOrigine && (
            <Champ terme="Période économique d’origine">{dateCourte(e.dateOrigine)}</Champ>
          )}
          {e.correctionAnterieure && (
            <Champ terme="Correction d’un exercice antérieur">
              exercice {e.exerciceOrigine} — l’ouverture n’est pas modifiée
            </Champ>
          )}
          <Champ terme="Validée le">
            {e.statut === 'validee' ? dateHeure(e.valideeLe) : <span className="text-amber-800">non validée</span>}
          </Champ>
          <Champ terme="Créée le">{dateHeure(e.creeLe)}</Champ>
          {e.processus && (
            <Champ terme="Produite par">
              {e.processus}
              {e.regleVersion && <span className="text-gray-500"> · règle {e.regleVersion}</span>}
              {e.sourceGeneration > 1 && <span className="text-gray-500"> · génération {e.sourceGeneration}</span>}
            </Champ>
          )}
          {e.corrigeeId && (
            <Champ terme="Extourne de">
              <Link to={ficheEcriture(e.corrigeeId)} className="inline-flex items-center gap-1 text-primary-700 hover:underline">
                <CornerUpLeft className="h-4 w-4" /> {references[e.corrigeeId] ?? 'l’écriture corrigée'}
              </Link>
            </Champ>
          )}
          {e.extourneeParId && (
            <Champ terme="Extournée par">
              <Link to={ficheEcriture(e.extourneeParId)} className="inline-flex items-center gap-1 text-primary-700 hover:underline">
                <CornerUpLeft className="h-4 w-4" /> {references[e.extourneeParId] ?? 'son extourne'}
              </Link>
            </Champ>
          )}
        </dl>
      </section>

      {/* --- Le contrôle des montants, pour un brouillon ---------------------- */}
      {controle && !controle.validable && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-medium">
            {e.statut === 'brouillon'
              ? 'Ce brouillon ne pourrait pas être validé en l’état :'
              : 'Les lignes lues ne passent pas le contrôle — la base l’aurait refusé ; à signaler :'}
          </p>
          <ul className="mt-1 list-disc pl-5">
            {controle.refus.map((r, i) => (
              <li key={`${r.etiquette}-${i}`}>
                {r.etiquette === '[desequilibre]'
                  ? `débit ${euros(controle.totalDebitCents)}, crédit ${euros(controle.totalCreditCents)} : écart de ${euros(Math.abs(controle.ecartCents))}`
                  : r.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* --- Les lignes ------------------------------------------------------ */}
      <div className="overflow-hidden rounded-xl bg-white shadow-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Compte</th>
                <th className="px-4 py-3">Tiers</th>
                <th className="px-4 py-3">Libellé</th>
                <th className="px-4 py-3 text-right">Débit</th>
                <th className="px-4 py-3 text-right">Crédit</th>
                <th className="px-4 py-3">Analytique</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {lignes.map((l) => {
                const a = analytiqueDeLaLigne(l);
                return (
                  <tr key={l.id} className="align-top">
                    <td className="px-4 py-3 tabular-nums text-gray-500">{l.position}</td>
                    <td className="px-4 py-3">
                      <Link to={lienVersLesEcritures({ compte: l.compteCode, exercice: e.exercice })}
                            className="font-medium tabular-nums text-primary-700 hover:underline"
                            title="Les écritures qui mouvementent ce compte">
                        {l.compteCode}
                      </Link>
                      <span className="block text-xs text-gray-600">{l.compteLibelle}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{l.tiersNom ?? <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-3 text-gray-700">{l.libelle ?? ''}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                      {l.debitCents > 0 ? euros(l.debitCents) : ''}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                      {l.creditCents > 0 ? euros(l.creditCents) : ''}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {a.axes.length === 0 ? (
                        a.resultatSansVentilation
                          ? <span className="text-amber-800">aucune ventilation</span>
                          : <span className="text-gray-400">—</span>
                      ) : (
                        <ul className="space-y-0.5">
                          {a.axes.map((axe) => (
                            <li key={axe.axeCode}>
                              <span className="text-gray-500">{axe.axeLibelle} :</span>{' '}
                              {axe.valeurs.map((v) => `${v.valeurLibelle} ${euros(v.montantCents)}`).join(', ')}
                              {axe.resteCents !== 0 && (
                                <span className="ml-1 font-medium text-amber-800">
                                  · {euros(axe.resteCents)} non ventilé
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {controle && (
              <tfoot className="bg-gray-50 text-sm font-medium">
                <tr>
                  <td className="px-4 py-3" colSpan={4}>
                    Total — {lignes.length} ligne{lignes.length > 1 ? 's' : ''}
                    {controle.ecartCents === 0
                      ? <span className="ml-2 font-normal text-emerald-700">débit = crédit</span>
                      : <span className="ml-2 font-normal text-red-700">écart de {euros(Math.abs(controle.ecartCents))}</span>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">{euros(controle.totalDebitCents)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">{euros(controle.totalCreditCents)}</td>
                  <td className="px-4 py-3" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </>
  );
}

function Champ({ terme, children }: { terme: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{terme}</dt>
      <dd className="mt-0.5 text-gray-900">{children}</dd>
    </div>
  );
}
